import { css } from 'aphrodite';
import * as React from 'react';
import {
	useSupportMailboxQuery,
	useUpdateSupportMailboxMutation,
} from '../../../queries/Support/useSupportMailboxQuery';
import { getApiErrorMessage, isForbiddenError } from '../../../utils/Support/supportHelpers';
import { Button } from '../../../web/components-v2/Button';
import { Input } from '../../../web/components-v2/Input';
import { mailboxEditFormStyles as styles } from './styles/mailboxEditFormStyles';
import { settingsStyles as st } from './styles/settingsStyles';

interface IMailboxEditFormProps {
	mailboxId: string;
	/** Optional back affordance when rendered inside the management list. */
	onBack?: () => void;
}

/**
 * Single-mailbox edit panel. Loads one mailbox via `useSupportMailboxQuery` and
 * persists changes with `useUpdateSupportMailboxMutation`.
 *
 * The GET now returns 403 for callers who are not an agent on the mailbox, so a
 * forbidden response renders a friendly "not an agent" state rather than an
 * infinite spinner or a raw error.
 */
export function MailboxEditForm({ mailboxId, onBack }: IMailboxEditFormProps) {
	const { data: mailbox, isLoading, isError, error } = useSupportMailboxQuery(mailboxId);
	const updateMutation = useUpdateSupportMailboxMutation(mailboxId);

	const [name, setName] = React.useState('');
	const [isActive, setIsActive] = React.useState(true);
	const [aliases, setAliases] = React.useState<string[]>([]);
	const [newAlias, setNewAlias] = React.useState('');
	const [reopenWindowDays, setReopenWindowDays] = React.useState(30);
	const [isDirty, setIsDirty] = React.useState(false);

	// Seed the form from server data ONCE per selected mailbox. A background refetch
	// (e.g. refetchOnWindowFocus) returns a new `mailbox` reference, and we must not
	// clobber unsaved edits when that happens — only hydrate on the first load or when
	// the caller swaps to a different mailboxId.
	const hydratedForIdRef = React.useRef<string | null>(null);
	React.useEffect(() => {
		if (mailbox && hydratedForIdRef.current !== mailbox.id) {
			setName(mailbox.name || '');
			setIsActive(mailbox.isActive ?? true);
			setAliases(mailbox.aliasAddresses || []);
			setReopenWindowDays(mailbox.reopenWindowDays ?? 30);
			setIsDirty(false);
			hydratedForIdRef.current = mailbox.id;
		}
	}, [mailbox]);

	const handleAddAlias = () => {
		const trimmed = newAlias.trim();
		if (!trimmed || aliases.includes(trimmed)) return;
		setAliases([...aliases, trimmed]);
		setNewAlias('');
		setIsDirty(true);
	};

	const handleRemoveAlias = (index: number) => {
		setAliases(aliases.filter((_, i) => i !== index));
		setIsDirty(true);
	};

	const handleSave = () => {
		if (!mailbox) return;
		// Partial PUT: send only the inbound/behavior fields this section owns.
		// Outbound fields (fromName/replyToAddress/signature) are preserved server-side.
		updateMutation.mutate(
			{
				name: name.trim(),
				isActive,
				aliasAddresses: aliases,
				reopenWindowDays,
			},
			{
				onSuccess: () => {
					setIsDirty(false);
				},
			}
		);
	};

	if (isLoading) return <div className={css(st.loading)}>Loading mailbox settings...</div>;

	// A 403 means the caller is not an agent on this mailbox — show a friendly
	// state instead of a generic failure (and never an infinite spinner).
	if (isError && isForbiddenError(error)) {
		return (
			<div className={css(styles.notice)}>
				<h2 className={css(styles.noticeTitle)}>You are not an agent on this mailbox</h2>
				<p className={css(styles.noticeBody)}>
					Ask a mailbox administrator to add you as an agent before configuring this mailbox.
				</p>
				{onBack && (
					<Button variant='outline' size='sm' onClick={onBack}>
						Back to mailboxes
					</Button>
				)}
			</div>
		);
	}

	if (isError) {
		return <div className={css(st.error)}>{getApiErrorMessage(error, 'Failed to load mailbox settings.')}</div>;
	}

	return (
		<div className={css(styles.section)}>
			{onBack && (
				<Button variant='ghost' size='sm' onClick={onBack} styles={[styles.backButton]}>
					&larr; Back to mailboxes
				</Button>
			)}
			<h2 className={css(st.sectionTitle)}>General</h2>
			<div className={css(st.sectionDescription)}>Basic configuration for this mailbox.</div>

			<div className={css(st.card)}>
				<div className={css(st.field)}>
					<label className={css(st.fieldLabel)}>Mailbox Name</label>
					<Input
						type='text'
						value={name}
						onChange={e => {
							setName(e.target.value);
							setIsDirty(true);
						}}
						styles={[st.input]}
					/>
				</div>

				<div className={css(st.field)}>
					<label className={css(st.fieldLabel)}>Email Address</label>
					<div className={css(styles.readonlyRow)}>
						<span className={css(styles.readonlyValue)}>{mailbox?.emailAddress || '--'}</span>
						<span className={css(styles.readonlyTag)}>Read-only · managed by Levitate</span>
					</div>
				</div>

				<div className={css(st.field)}>
					<label className={css(st.fieldLabel)}>Alias Addresses</label>
					{aliases.length > 0 && (
						<div className={css(styles.aliasList)}>
							{aliases.map((alias, i) => (
								<div key={i} className={css(styles.aliasChip)}>
									<span>{alias}</span>
									<Button
										type='button'
										variant='ghost'
										size='icon'
										styles={[styles.aliasRemove]}
										onClick={() => handleRemoveAlias(i)}
										title='Remove'
										aria-label={`Remove ${alias}`}
									>
										&times;
									</Button>
								</div>
							))}
						</div>
					)}
					<div className={css(styles.aliasInputRow)}>
						<Input
							type='text'
							value={newAlias}
							onChange={e => setNewAlias(e.target.value)}
							onKeyDown={e => {
								if (e.key === 'Enter') handleAddAlias();
							}}
							placeholder='Add alias email…'
							styles={[st.input, styles.aliasInput]}
						/>
						<Button onClick={handleAddAlias} disabled={!newAlias.trim()}>
							Add
						</Button>
					</div>
				</div>

				<div className={css(st.field)}>
					<label className={css(st.fieldLabel)}>Reopen Window (days)</label>
					<Input
						type='number'
						value={String(reopenWindowDays)}
						onChange={e => {
							setReopenWindowDays(Math.max(0, parseInt(e.target.value, 10) || 0));
							setIsDirty(true);
						}}
						styles={[st.input]}
					/>
					<div className={css(st.hint)}>
						Customer replies within this many days of closing reopen the ticket. Later replies create a new linked
						ticket. Set to 0 to never reopen.
					</div>
				</div>

				<div className={css(st.toggleRow, styles.activeRow)}>
					<Button
						type='button'
						variant='ghost'
						styles={[st.toggle, isActive && st.toggleActive]}
						onClick={() => {
							setIsActive(!isActive);
							setIsDirty(true);
						}}
						aria-pressed={isActive}
						aria-label='Active'
					>
						<span className={css(st.toggleKnob, isActive && st.toggleKnobActive)} />
					</Button>
					<div className={css(st.toggleText)}>
						<span className={css(st.toggleTitle)}>Active</span>
						<span className={css(st.toggleDescription)}>Inactive mailboxes stop receiving new mail.</span>
					</div>
				</div>

				<div className={css(st.formActions)}>
					<Button onClick={handleSave} disabled={updateMutation.isPending || !isDirty}>
						{updateMutation.isPending ? 'Saving...' : 'Save Changes'}
					</Button>
					{updateMutation.isSuccess && !isDirty && <span className={css(st.successMsg)}>Settings saved.</span>}
					{updateMutation.isError && (
						<span className={css(st.errorMsg)}>
							{getApiErrorMessage(updateMutation.error, 'Failed to save settings.')}
						</span>
					)}
				</div>
			</div>
		</div>
	);
}
